import { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

interface Modifier {
  id: string;
  name: string;
  priceAdjustmentCents: number;
}

interface ModifierGroup {
  id: string;
  name: string;
  required: boolean;
  minSelections: number;
  maxSelections: number;
  modifiers: Modifier[];
}

interface MenuItem {
  id: string;
  name: string;
  shortName?: string;
  priceCents: number;
  modifierGroups: ModifierGroup[];
}

interface SelectedModifier {
  id: string;
  name: string;
  priceCents: number;
  groupId: string;
}

interface ModifierModalProps {
  item: MenuItem;
  onConfirm: (modifiers: SelectedModifier[], specialInstructions: string) => void;
  onCancel: () => void;
}

export default function ModifierModal({ item, onConfirm, onCancel }: ModifierModalProps) {
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, string[]>>({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize required groups
  useEffect(() => {
    const initial: Record<string, string[]> = {};
    item.modifierGroups.forEach((group) => {
      initial[group.id] = [];
    });
    setSelectedModifiers(initial);
  }, [item]);

  const formatCurrency = (cents: number) => {
    if (cents === 0) return '';
    return cents > 0 ? `+$${(cents / 100).toFixed(2)}` : `-$${(Math.abs(cents) / 100).toFixed(2)}`;
  };

  const toggleModifier = (group: ModifierGroup, modifierId: string) => {
    setErrors((prev) => ({ ...prev, [group.id]: '' }));

    const currentSelections = selectedModifiers[group.id] || [];
    const isSelected = currentSelections.includes(modifierId);

    if (isSelected) {
      // Remove selection
      setSelectedModifiers((prev) => ({
        ...prev,
        [group.id]: currentSelections.filter((id) => id !== modifierId),
      }));
    } else {
      // Add selection
      if (group.maxSelections === 1) {
        // Radio-style: replace
        setSelectedModifiers((prev) => ({
          ...prev,
          [group.id]: [modifierId],
        }));
      } else if (currentSelections.length < group.maxSelections) {
        // Multi-select: add if under limit
        setSelectedModifiers((prev) => ({
          ...prev,
          [group.id]: [...currentSelections, modifierId],
        }));
      }
    }
  };

  const isModifierSelected = (groupId: string, modifierId: string) => {
    return (selectedModifiers[groupId] || []).includes(modifierId);
  };

  const validateSelections = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    item.modifierGroups.forEach((group) => {
      const selections = selectedModifiers[group.id] || [];
      if (group.required && selections.length < group.minSelections) {
        newErrors[group.id] = `Please select at least ${group.minSelections} option${group.minSelections > 1 ? 's' : ''}`;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleConfirm = () => {
    if (!validateSelections()) return;

    // Convert to flat list of modifiers
    const modifiers: SelectedModifier[] = [];
    Object.entries(selectedModifiers).forEach(([groupId, modifierIds]) => {
      const group = item.modifierGroups.find((g) => g.id === groupId);
      if (!group) return;

      modifierIds.forEach((modId) => {
        const modifier = group.modifiers.find((m) => m.id === modId);
        if (modifier) {
          modifiers.push({
            id: modifier.id,
            name: modifier.name,
            priceCents: modifier.priceAdjustmentCents,
            groupId,
          });
        }
      });
    });

    onConfirm(modifiers, specialInstructions);
  };

  const calculateTotal = () => {
    let total = item.priceCents;
    Object.entries(selectedModifiers).forEach(([groupId, modifierIds]) => {
      const group = item.modifierGroups.find((g) => g.id === groupId);
      if (!group) return;

      modifierIds.forEach((modId) => {
        const modifier = group.modifiers.find((m) => m.id === modId);
        if (modifier) {
          total += modifier.priceAdjustmentCents;
        }
      });
    });
    return total;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] max-h-[90dvh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{item.name}</h2>
            <p className="text-primary-600 font-semibold">
              ${(item.priceCents / 100).toFixed(2)}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Modifier Groups */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {item.modifierGroups.map((group) => (
            <div key={group.id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{group.name}</h3>
                  <p className="text-sm text-slate-500">
                    {group.required ? (
                      <span className="text-danger-500">Required</span>
                    ) : (
                      'Optional'
                    )}
                    {group.maxSelections > 1 && ` • Select up to ${group.maxSelections}`}
                  </p>
                </div>
                {errors[group.id] && (
                  <div className="flex items-center gap-1 text-danger-500 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors[group.id]}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {group.modifiers.map((modifier) => {
                  const isSelected = isModifierSelected(group.id, modifier.id);
                  return (
                    <button
                      key={modifier.id}
                      onClick={() => toggleModifier(group, modifier.id)}
                      className={clsx(
                        'w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all',
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-slate-200 hover:border-slate-300'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={clsx(
                            'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors',
                            isSelected
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-slate-300'
                          )}
                        >
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <span className={clsx(
                          'font-medium',
                          isSelected ? 'text-primary-700' : 'text-slate-700'
                        )}>
                          {modifier.name}
                        </span>
                      </div>
                      {modifier.priceAdjustmentCents !== 0 && (
                        <span className={clsx(
                          'text-sm font-medium',
                          modifier.priceAdjustmentCents > 0 ? 'text-slate-600' : 'text-success-600'
                        )}>
                          {formatCurrency(modifier.priceAdjustmentCents)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Special Instructions */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Special Instructions</h3>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="e.g., No onions, extra sauce, allergy notes..."
              className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 space-y-3">
          <div className="flex items-center justify-between text-lg font-semibold">
            <span>Total</span>
            <span className="text-primary-600">${(calculateTotal() / 100).toFixed(2)}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="btn btn-primary flex-1"
            >
              Add to Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
